import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from './lib/queryClient';
import { useAuthStore } from './store/authStore';
import Layout from './components/Layout';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import AcceptInvitation from './pages/auth/AcceptInvitation';

// Main Pages
import Dashboard from './pages/Dashboard';
import PaymentList from './pages/payments/PaymentList';
import PaymentDetail from './pages/payments/PaymentDetail';
import Risks from './pages/Risks';
import Reports from './pages/Reports';
import Team from './pages/Team';
import AuditLogs from './pages/AuditLogs';
import Pricing from './pages/Pricing';

// Intelligence Pages
import Patterns from './pages/intelligence/Patterns';
import PatternDetail from './pages/intelligence/PatternDetail';

// Settings Pages
import ApiKeys from './pages/settings/ApiKeys';
import Organization from './pages/settings/Organization';
import Providers from './pages/settings/Providers';
import AiSettings from './pages/settings/AiSettings';
import { Profile } from './pages/settings/Profile';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return <Layout>{children}</Layout>;
};

// Public Route (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
};

const App: React.FC = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                    <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                    <Route path="/accept-invitation" element={<AcceptInvitation />} />

                    {/* Protected Routes */}
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/payments" element={<ProtectedRoute><PaymentList /></ProtectedRoute>} />
                    <Route path="/payments/:id" element={<ProtectedRoute><PaymentDetail /></ProtectedRoute>} />
                    <Route path="/risks" element={<ProtectedRoute><Risks /></ProtectedRoute>} />
                    <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                    <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
                    <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
                    <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />

                    {/* Intelligence Routes */}
                    <Route path="/intelligence/patterns" element={<ProtectedRoute><Patterns /></ProtectedRoute>} />
                    <Route path="/intelligence/patterns/:id" element={<ProtectedRoute><PatternDetail /></ProtectedRoute>} />

                    {/* Settings Routes */}
                    <Route path="/settings" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/settings/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/settings/api-keys" element={<ProtectedRoute><ApiKeys /></ProtectedRoute>} />
                    <Route path="/settings/organization" element={<ProtectedRoute><Organization /></ProtectedRoute>} />
                    <Route path="/settings/providers" element={<ProtectedRoute><Providers /></ProtectedRoute>} />
                    <Route path="/settings/ai" element={<ProtectedRoute><AiSettings /></ProtectedRoute>} />

                    {/* Default Route */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </BrowserRouter>

            {/* Toast Notifications */}
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#fff',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        padding: '16px',
                        borderRadius: '8px',
                    },
                    success: {
                        iconTheme: {
                            primary: '#10b981',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
        </QueryClientProvider>
    );
};

export default App;
